import { gql } from 'graphql-request';
import { FeatureType, ProjectStats } from 'src/shared/types';

/**
 * A project as the graph holds it — the shape `customerProjectsInBC` returns. This is the
 * only list that includes projects created by `admin.createProject`, since every other
 * project list is served by the project-definition service.
 */
export type GraphProject = {
    projectId: string;
    projectName: string | null;
    projectCode: string | null;
    leadName: string | null;
    leadEmail: string | null;
};

/**
 * `searchText` is non-null in the schema; an empty string is the established way to
 * mean "no filter" (see GetCustomerProjectsFromNeo callers), and omitting `limit`
 * lets the query fall back to its no-limit branch.
 */
export const buildGraphProjectsInput = (customerId: string) => ({
    input: { customerId, searchText: '' },
});

/**
 * Project definitions for a customer. `basicProjectList` is unpaginated, which the
 * merged table needs — the graph list it joins against is unpaginated too, and a
 * partial definition list would report projects as definition-less when they are not.
 */
export const customerProjectDefinitionsQuery = gql`
    query customer($customerId: String!) {
        customer(customerId: $customerId) {
            customerId
            basicProjectList {
                projectId
                projectName
                projectCode
                country
                status
                samplesCount
                features
                includeInPortfolio
            }
        }
    }
`;

export type CustomerProjectDefinitionsParams = {
    customerId: string;
};

export type CustomerProjectDefinitionsResult = {
    customer: {
        customerId: string;
        basicProjectList: (ProjectDefinitionListRow | null)[] | null;
    } | null;
};

export type ProjectDefinitionListRow = {
    projectId: string;
    projectName: string | null;
    projectCode: string | null;
    country: string | null;
    status: string | null;
    samplesCount: number | null;
    features: FeatureType[] | null;
    /** Whether this project is in its customer's Mongo-backed portfolio (DD-6079) - drives the
     * Nature Performance projects table's portfolio toggle column. */
    includeInPortfolio: boolean;
};

export type SetProjectPortfolioInclusionParams = {
    projectId: string;
    includeInPortfolio: boolean;
};

export type SetProjectPortfolioInclusionResult = {
    admin: {
        setProjectPortfolioInclusion: boolean;
    };
};

/**
 * Adds/removes one project from its customer's Mongo-backed portfolio (DD-6079) - a plain flag
 * flip (project_definition's includeInPortfolio), not a richer resolve-or-create-portfolio
 * operation. Invalidates the same 'project' tag customerProjectDefinitions provides, so the
 * table re-reads the real state from the server rather than trusting an optimistic local flip.
 */
export const setProjectPortfolioInclusionMutation = gql`
    mutation admin($projectId: String!, $includeInPortfolio: Boolean!) {
        admin {
            setProjectPortfolioInclusion(projectId: $projectId, includeInPortfolio: $includeInPortfolio)
        }
    }
`;

export const createProjectMutation = gql`
    mutation admin($customerId: String!, $projectId: String!, $projectName: String!, $projectLeadName: String, $projectLeadEmail: String) {
        admin {
            createProject(
                customerId: $customerId
                projectId: $projectId
                projectName: $projectName
                projectLeadName: $projectLeadName
                projectLeadEmail: $projectLeadEmail
            ) {
                projectId
                projectName
                projectLeadName
                projectLeadEmail
            }
        }
    }
`;

export type CreateProjectParams = {
    customerId: string;
    projectId: string;
    projectName: string;
    projectLeadName?: string;
    projectLeadEmail?: string;
};

export type CreateProjectResult = {
    admin: {
        createProject: {
            projectId: string;
            projectName: string | null;
            projectLeadName: string | null;
            projectLeadEmail: string | null;
        };
    };
};

/**
 * Trims every value and drops blank optional ones, so an untouched lead field stores
 * nothing rather than an empty string that later reads as "a lead with no name".
 */
export const buildCreateProjectVariables = (params: CreateProjectParams): CreateProjectParams => {
    const leadName = params.projectLeadName?.trim();
    const leadEmail = params.projectLeadEmail?.trim();

    return {
        customerId: params.customerId.trim(),
        projectId: params.projectId.trim(),
        projectName: params.projectName.trim(),
        ...(leadName ? { projectLeadName: leadName } : {}),
        ...(leadEmail ? { projectLeadEmail: leadEmail } : {}),
    };
};

export const updateProjectMutation = gql`
    mutation admin($projectId: String!, $projectName: String!) {
        admin {
            updateProject(projectId: $projectId, projectName: $projectName) {
                projectId
                projectName
            }
        }
    }
`;

export type UpdateProjectParams = {
    projectId: string;
    projectName: string;
};

export type UpdateProjectResult = {
    admin: {
        updateProject: {
            projectId: string;
            projectName: string | null;
        };
    };
};

export const deleteProjectMutation = gql`
    mutation admin($projectId: String!) {
        admin {
            deleteProject(projectId: $projectId) {
                projectId
                projectName
                sampleCount
                assetCount
                chartCount
                projectDefinitionDocsRemoved
                projectSitesDocsRemoved
                hlcaChartsDocsRemoved
            }
        }
    }
`;

export type DeleteProjectParams = {
    projectId: string;
};

/** What the cascade actually removed. Counts are the backend's own, not the pre-delete estimate. */
export type DeleteProjectResult = {
    admin: {
        deleteProject: {
            projectId: string;
            projectName: string;
            sampleCount: number;
            assetCount: number;
            chartCount: number;
            projectDefinitionDocsRemoved: number;
            projectSitesDocsRemoved: number;
            hlcaChartsDocsRemoved: number;
        };
    };
};

export const projectStatsQuery = gql`
    query project($projectId: String!) {
        project(projectId: $projectId) {
            projectId
            stats {
                sampleCount
                assetCount
                chartCount
                generatedAt
            }
        }
    }
`;

export type ProjectStatsParams = {
    projectId: string;
};

export type ProjectStatsResult = {
    project: {
        projectId: string;
        stats: ProjectStats;
    } | null;
};
